import { CheckCircleFilled, CheckOutlined } from "@ant-design/icons";
import { Button, Card, Col, message, Row, Spin, Typography } from "antd";
import {
  trackSelfCampaignTimelineCalendlyRedirected,
  trackSelfCampaignTimelineConfirmationViewed,
  trackSelfCampaignTimelineSelected,
  trackSelfCampaignTimelineSkipped,
  trackSelfCampaignTimelineStepViewed,
  type SelfCampaignTimelineAnalyticsContext,
} from "analytics";
import { useEffect, useMemo, useRef, useState } from "react";
import { PopupModal, useCalendlyEventListener } from "react-calendly";
import { useLocation } from "react-router-dom";
import { type LeadSubmissionUrgency } from "interfaces/lead-submission";
import { foodInfluencersCollabLink } from "helpers/calendly-helper";
import { updateLeadSubmissionUrgency } from "services/SauceService";
import SelfCampaignCreate from "./SelfCampaignCreate";
import SelfCampaignPageLayout from "./components/SelfCampaignPageLayout";
import { SELF_CAMPAIGN_TIMELINE_ENABLED } from "./timelineFeatureToggle";
import { useSelfCampaignLead } from "./useSelfCampaignLead";
import "./SelfCampaignEntry.scss";

type TimelineSelection =
  | "within_1_week"
  | "within_30_days"
  | "thirty_plus_days"
  | "skip";

const CONSULTATION_PHONE_LABEL = "(720) 817 5354";
const TIMELINE_URGENCY_BY_SELECTION: Record<
  TimelineSelection,
  LeadSubmissionUrgency
> = {
  within_1_week: "high",
  within_30_days: "medium",
  thirty_plus_days: "low",
  skip: "skipped",
};

const SelfCampaignEntry: React.FC = () => {
  const location = useLocation();
  const {
    createdLeadSubmissionId,
    leadSubmissionId,
    loadingLeadSubmission,
    queryParams,
  } = useSelfCampaignLead({
    mode: "create",
    shouldFetchLeadSubmission: false,
  });

  const analyticsContext = useMemo<SelfCampaignTimelineAnalyticsContext>(() => {
    return {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search]);

  const hasLeadSubmissionId = Boolean(leadSubmissionId);
  const hasNewlyCreatedLeadSubmission = Boolean(
    createdLeadSubmissionId && createdLeadSubmissionId === leadSubmissionId,
  );
  const hasBootstrapQueryParams = Boolean(
    queryParams.email && queryParams.businessName && queryParams.phone,
  );

  const shouldBypassTimeline =
    !SELF_CAMPAIGN_TIMELINE_ENABLED ||
    (hasLeadSubmissionId && !hasNewlyCreatedLeadSubmission);

  const [timelineCompleted, setTimelineCompleted] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [calendlyPopupOpen, setCalendlyPopupOpen] = useState<boolean>(false);
  const [savingUrgency, setSavingUrgency] = useState<boolean>(false);

  const hasTrackedTimelineViewRef = useRef<boolean>(false);
  const hasTrackedConfirmationViewRef = useRef<boolean>(false);

  const rootElement = useMemo(() => {
    if (typeof document === "undefined") {
      return null;
    }

    return (document.getElementById("root") || document.body) as HTMLElement;
  }, []);

  useCalendlyEventListener({
    onEventScheduled: () => {
      if (!calendlyPopupOpen) {
        return;
      }

      setCalendlyPopupOpen(false);
      setTimelineCompleted(true);
    },
  });

  useEffect(() => {
    if (
      shouldBypassTimeline ||
      loadingLeadSubmission ||
      timelineCompleted ||
      showConfirmation ||
      hasTrackedTimelineViewRef.current
    ) {
      return;
    }

    hasTrackedTimelineViewRef.current = true;
    trackSelfCampaignTimelineStepViewed(analyticsContext);
  }, [
    analyticsContext,
    loadingLeadSubmission,
    shouldBypassTimeline,
    showConfirmation,
    timelineCompleted,
  ]);

  useEffect(() => {
    if (!showConfirmation || hasTrackedConfirmationViewRef.current) {
      return;
    }

    hasTrackedConfirmationViewRef.current = true;
    trackSelfCampaignTimelineConfirmationViewed(analyticsContext);
  }, [analyticsContext, showConfirmation]);

  const saveTimelineUrgency = async (
    selection: TimelineSelection,
  ): Promise<boolean> => {
    if (!leadSubmissionId) {
      message.error("We could not save your timeline. Please try again.");
      return false;
    }

    try {
      setSavingUrgency(true);
      await updateLeadSubmissionUrgency(leadSubmissionId, {
        urgency: TIMELINE_URGENCY_BY_SELECTION[selection],
      });
      return true;
    } catch (_error) {
      message.error("We could not save your timeline. Please try again.");
      return false;
    } finally {
      setSavingUrgency(false);
    }
  };

  const handleTimelineSelection = async (
    selection: TimelineSelection,
  ): Promise<void> => {
    if (savingUrgency) {
      return;
    }

    trackSelfCampaignTimelineSelected(analyticsContext, {
      timeline: selection,
    });

    const savedUrgency = await saveTimelineUrgency(selection);
    if (!savedUrgency) {
      return;
    }

    if (selection === "within_1_week") {
      setShowConfirmation(true);
      return;
    }

    if (selection === "within_30_days") {
      trackSelfCampaignTimelineCalendlyRedirected(analyticsContext);
      setCalendlyPopupOpen(true);
      return;
    }

    if (selection === "skip") {
      trackSelfCampaignTimelineSkipped(analyticsContext);
    }

    setTimelineCompleted(true);
  };

  if (loadingLeadSubmission || (hasBootstrapQueryParams && !leadSubmissionId)) {
    return (
      <div className="self-campaign-entry__full-page-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (shouldBypassTimeline || timelineCompleted) {
    return <SelfCampaignCreate />;
  }

  if (showConfirmation) {
    return (
      <SelfCampaignPageLayout
        variant="create"
        rootClassName="self-campaign-entry"
      >
        <section className="self-campaign-entry__confirmation-section">
          <div className="self-campaign-entry__confirmation-badge-wrap">
            <CheckCircleFilled className="self-campaign-entry__confirmation-icon" />
          </div>

          <Typography.Title
            level={2}
            className="self-campaign-entry__confirmation-title"
          >
            Consultation Request Received!
          </Typography.Title>

          <Typography.Paragraph className="self-campaign-entry__confirmation-subtitle">
            Our influencer specialist will reach out shortly for your
            complimentary call.
          </Typography.Paragraph>

          <Card className="self-campaign-entry__phone-card" variant="outlined">
            <Typography.Text className="self-campaign-entry__next-step-label">
              NEXT STEP
            </Typography.Text>

            <Typography.Title
              level={4}
              className="self-campaign-entry__phone-card-title"
            >
              Expect a call from this number:
            </Typography.Title>

            <div className="self-campaign-entry__phone-highlight">
              {CONSULTATION_PHONE_LABEL}
            </div>

            <Typography.Paragraph className="self-campaign-entry__phone-note">
              Please save this number so you don&apos;t miss our specialist.
            </Typography.Paragraph>
          </Card>

          <Row
            gutter={[16, 16]}
            className="self-campaign-entry__confirmation-grid"
          >
            <Col xs={24} md={12}>
              <Card
                className="self-campaign-entry__info-card"
                variant="outlined"
              >
                <Typography.Title
                  level={4}
                  className="self-campaign-entry__info-title"
                >
                  Local Influencer Impact
                </Typography.Title>
                <Typography.Paragraph className="self-campaign-entry__info-text">
                  We&apos;ll show you exactly how to leverage local influencers
                  to drive real foot traffic and paying customers directly to
                  your business doors.
                </Typography.Paragraph>
                <ul className="self-campaign-entry__info-list">
                  <li>
                    <CheckOutlined /> Targeted demographic reach
                  </li>
                  <li>
                    <CheckOutlined /> Authentic community engagement
                  </li>
                </ul>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card
                className="self-campaign-entry__info-card"
                variant="outlined"
              >
                <Typography.Title
                  level={4}
                  className="self-campaign-entry__info-title"
                >
                  Campaign Strategy Breakdown
                </Typography.Title>
                <Typography.Paragraph className="self-campaign-entry__info-text">
                  We&apos;ll break down what successful campaigns look like, why
                  most DIY efforts fall short, and the precise steps to do it
                  differently.
                </Typography.Paragraph>
                <ul className="self-campaign-entry__info-list">
                  <li>
                    <CheckOutlined /> ROI measurement frameworks
                  </li>
                  <li>
                    <CheckOutlined /> Avoiding common pitfalls
                  </li>
                </ul>
              </Card>
            </Col>
          </Row>
        </section>
      </SelfCampaignPageLayout>
    );
  }

  return (
    <SelfCampaignPageLayout
      variant="create"
      rootClassName="self-campaign-entry"
    >
      <section className="self-campaign-entry__timeline-section">
        <Typography.Title
          level={2}
          className="self-campaign-entry__timeline-title"
        >
          When do you want your first creators?
        </Typography.Title>
        <Typography.Paragraph className="self-campaign-entry__timeline-subtitle">
          <span>We&apos;ll line up creators based on your timeline</span>
          <span>Most businesses go live within 3 days</span>
          <span>You can always change this later</span>
        </Typography.Paragraph>

        <div className="self-campaign-entry__options-grid">
          <button
            type="button"
            className="self-campaign-entry__option-card self-campaign-entry__option-card--primary"
            disabled={savingUrgency}
            onClick={() => {
              void handleTimelineSelection("within_1_week");
            }}
          >
            <span className="self-campaign-entry__option-title">
              Within 1 Week
            </span>
            <span className="self-campaign-entry__option-copy">
              Plan and launch soon
            </span>
          </button>

          <button
            type="button"
            className="self-campaign-entry__option-card"
            disabled={savingUrgency}
            onClick={() => {
              void handleTimelineSelection("within_30_days");
            }}
          >
            <span className="self-campaign-entry__option-title">
              Within 30 Days
            </span>
            <span className="self-campaign-entry__option-copy">
              Explore and prepare
            </span>
          </button>

          <button
            type="button"
            className="self-campaign-entry__option-card"
            disabled={savingUrgency}
            onClick={() => {
              void handleTimelineSelection("thirty_plus_days");
            }}
          >
            <span className="self-campaign-entry__option-title">30+ Days</span>
            <span className="self-campaign-entry__option-copy">
              No rush, just exploring
            </span>
          </button>
        </div>

        <Button
          type="link"
          className="self-campaign-entry__skip"
          disabled={savingUrgency}
          onClick={() => {
            void handleTimelineSelection("skip");
          }}
        >
          Not sure yet? <span>Skip for now &rarr;</span>
        </Button>
      </section>
      {rootElement ? (
        <PopupModal
          url={foodInfluencersCollabLink}
          open={calendlyPopupOpen}
          onModalClose={() => setCalendlyPopupOpen(false)}
          rootElement={rootElement}
          prefill={{
            name: queryParams.name || "",
            email: queryParams.email || "",
            customAnswers: {
              a2: queryParams.businessName || "",
            },
          }}
        />
      ) : null}
    </SelfCampaignPageLayout>
  );
};

export default SelfCampaignEntry;
